import React, { useCallback, useEffect, useState } from "react";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import Swal from "sweetalert2";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const buildPrintHtml = ({ stats, gender }) => {
  const totalMedals = (stats.medalsByLevel || []).reduce((sum, l) => sum + Number(l.count || 0), 0);
  const medalsByTypeRows = (stats.medalsByLevel || [])
    .map(
      (row, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(row.medal_type || "-")}</td>
          <td>${Number(row.count || 0)}</td>
        </tr>
      `
    )
    .join("");
  const yearRows = (stats.medalsYearly || [])
    .map(
      (row, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(row.year || "-")}</td>
          <td>${Number(row.count || 0)}</td>
        </tr>
      `
    )
    .join("");

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Reports Print Preview</title>
        <style>
          body { font-family: Arial, sans-serif; color: #111827; padding: 18px; }
          h1 { margin: 0 0 6px; font-size: 22px; }
          .meta { margin-bottom: 10px; color: #4b5563; font-size: 12px; }
          .cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 12px; }
          .card { border: 1px solid #d1d5db; border-radius: 8px; padding: 10px; }
          .card h3 { margin: 0; font-size: 12px; color: #6b7280; font-weight: 600; }
          .card p { margin: 6px 0 0; font-size: 18px; font-weight: 700; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; }
          th, td { border: 1px solid #d1d5db; padding: 6px; text-align: left; }
          th { background: #f3f4f6; }
          .section-title { font-size: 14px; font-weight: 700; margin: 10px 0 6px; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <h1>Para Sports Reports</h1>
        <div class="meta">
          Gender Filter: ${escapeHtml(gender)} |
          Generated: ${escapeHtml(new Date().toLocaleString())}
        </div>
        <div class="cards">
          <div class="card"><h3>Total Players</h3><p>${Number(stats.totalPlayers || 0)}</p></div>
          <div class="card"><h3>Total Medals</h3><p>${totalMedals}</p></div>
          <div class="card"><h3>Latest Year Medals</h3><p>${Number(stats.medalsYearly?.[0]?.count || 0)}</p></div>
        </div>

        <div class="section-title">Medals by Type</div>
        <table>
          <thead><tr><th>#</th><th>Type</th><th>Count</th></tr></thead>
          <tbody>${medalsByTypeRows || '<tr><td colspan="3">No data</td></tr>'}</tbody>
        </table>

        <div class="section-title">Medals Year-wise</div>
        <table>
          <thead><tr><th>#</th><th>Year</th><th>Count</th></tr></thead>
          <tbody>${yearRows || '<tr><td colspan="3">No data</td></tr>'}</tbody>
        </table>
      </body>
    </html>
  `;
};

const ReportsDashboard = () => {
  const [stats, setStats] = useState({
    totalPlayers: 0,
    medalsByLevel: [],
    medalsYearly: []
  });
  const [gender, setGender] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exportingKey, setExportingKey] = useState("");

  const loadReports = useCallback(async () => {
  try {
    setLoading(true);
    const params = { gender };
    const playersRes = await API.fetchPlayersCount(params);
    const medalsLevelRes = await API.fetchMedalsByLevel(params);
    const medalsYearlyRes = await API.fetchMedalsYearly(params);

    setStats({
      totalPlayers: playersRes?.total || playersRes?.data?.total || 0,
      medalsByLevel: medalsLevelRes?.data || [],
      medalsYearly: medalsYearlyRes?.data || []
    });
  } catch (err) {
    console.error("Reports error:", err);
    Swal.fire("Failed to load reports");
  } finally {
    setLoading(false);
  }
}, [gender]);


  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const exportReport = async (report, format) => {
    const key = `${report}-${format}`;
    try {
      setExportingKey(key);
      await API.downloadReport(report, format, { gender });
      Swal.fire("Downloaded", `${format.toUpperCase()} report downloaded`, "success");
    } catch (err) {
      Swal.fire("Export failed", err?.message || "Unable to export report", "error");
    } finally {
      setExportingKey("");
    }
  };

  const openPrintPreview = () => {
    const html = buildPrintHtml({ stats, gender });
    const printWindow = window.open("", "_blank", "width=1100,height=780");

    if (printWindow && printWindow.document) {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
      return;
    }

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    iframe.srcdoc = html;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
        }, 1000);
      }
    };

    Swal.fire("Popup blocked", "Opened print preview using browser fallback.", "info");
  };

  const ExportButtons = ({ report }) => (
    <div className="mt-4 flex gap-2">
      <button
        onClick={() => exportReport(report, "excel")}
        disabled={exportingKey === `${report}-excel`}
        className="rounded-lg bg-white/20 px-3 py-1 text-xs font-semibold text-white hover:bg-white/30 disabled:opacity-60"
      >
        {exportingKey === `${report}-excel` ? "Exporting..." : "Excel"}
      </button>
      <button
        onClick={() => exportReport(report, "pdf")}
        disabled={exportingKey === `${report}-pdf`}
        className="rounded-lg bg-white/20 px-3 py-1 text-xs font-semibold text-white hover:bg-white/30 disabled:opacity-60"
      >
        {exportingKey === `${report}-pdf` ? "Exporting..." : "PDF"}
      </button>
    </div>
  );

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
            📊 Para Sports Reports
          </h1>
          <p className="text-xl text-gray-600">Player Statistics • Medals • Achievements</p>
          <div className="mt-4 inline-flex items-center gap-2">
            <label className="text-sm font-semibold text-gray-700">Gender:</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All</option>
              <option value="men">Men</option>
              <option value="women">Women</option>
            </select>
            <button
              onClick={openPrintPreview}
              disabled={loading}
              className="rounded-lg bg-gray-800 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-900 disabled:opacity-60"
            >
              Print Preview
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-emerald-600 text-4xl animate-spin">📊</div>
          </div>
        ) : (
          <>
            {/* Key Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-8 rounded-3xl shadow-2xl">
                <div className="text-4xl mb-4">👥</div>
                <div className="text-4xl font-bold mb-2">{stats.totalPlayers.toLocaleString()}</div>
                <div className="text-blue-100 font-semibold">Total Para Players</div>
                <ExportButtons report="players-count" />
              </div>
              
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-8 rounded-3xl shadow-2xl">
                <div className="text-4xl mb-4">🏆</div>
                <div className="text-4xl font-bold mb-2">
                  {stats.medalsByLevel.reduce((sum, l) => sum + Number(l.count || 0), 0)}
                </div>
                <div className="text-emerald-100 font-semibold">Total Medals Won</div>
                <ExportButtons report="medals-by-level" />
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-8 rounded-3xl shadow-2xl">
                <div className="text-4xl mb-4">📈</div>
                <div className="text-4xl font-bold mb-2">
                  {stats.medalsYearly[0]?.count || 0}
                </div>
                <div className="text-purple-100 font-semibold">
                  Medals {stats.medalsYearly[0]?.year || 'This Year'}
                </div>
                <ExportButtons report="medals-yearly" />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Medals by Level */}
              <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <span className="text-3xl mr-3">📍</span>
                  Medals by Type
                </h2>
                
                <div className="space-y-4">
                  {stats.medalsByLevel.map((level, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl">
                      <div className="font-bold text-lg text-gray-900">{level.medal_type}</div>
                      <div className="text-2xl font-bold text-emerald-600">
                        {level.count}
                      </div>
                    </div>
                  ))}
                  {stats.medalsByLevel.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No medals data yet
                    </div>
                  )}
                </div>
              </div>

              {/* Yearly Medals */}
              <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <span className="text-3xl mr-3">📅</span>
                  Medals Year-wise
                </h2>
                
                <div className="space-y-3">
                  {stats.medalsYearly.slice(0, 5).map((year, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl">
                      <div className="font-bold text-lg text-gray-900">{year.year}</div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-600">{year.count}</div>
                      </div>
                    </div>
                  ))}
                  {stats.medalsYearly.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      Add achievements to see yearly stats
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-6 py-3 rounded-2xl font-semibold">
                📈 Reports auto-update when you add players/achievements
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ReportsDashboard;
